import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DataTable, Divider, InlineStack, Select, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import axios from "axios";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminAtdAttributeMetafieldMapping, AdminAtdConnectionTestResult, AdminAtdCreateTestProductResponse, AdminAtdFilteredProductSample, AdminAtdProfile, AdminFitmentImportHistoryItem, AdminShopRuntime, AdminStoreDetails, AdminStoreGeneralSettings, AdminStoreNote } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const defaultAtdProfile: AdminAtdProfile = {
  name: "Primary ATD Profile",
  baseUrl: "https://testws.atdconnect.com",
  apiVersion: "3.6",
  shipToHomeApiVersion: "1.1",
  clientId: "",
  username: "",
  password: "",
  secretKey: "",
  accountNumber: "",
  locationNumber: "",
  useShipToHomeCatalog: false,
  isActive: true,
  enableAtdPull: true,
  enableAtdPushToShopify: true,
  enableFitmentPull: true,
  enableFitmentPushToShopify: true,
  enablePricePull: true,
  enablePricePushToShopify: true,
  createProductsAsDraft: false,
  testModeEnabled: false,
  testModeCreateLimit: 25,
  sizeAttributeValues: [],
  attributeMetafieldMappings: [],
};

const atdMetafieldTypeOptions = [
  { label: "Single line text", value: "single_line_text_field" },
  { label: "Integer", value: "number_integer" },
  { label: "Decimal", value: "number_decimal" },
  { label: "Dimension", value: "dimension" },
  { label: "Weight", value: "weight" },
  { label: "True/False", value: "boolean" },
];

const syncSettingMetadata: Record<string, { label: string; description: string }> = {
  "Shopify.AccessTokenAuthorizationHold": {
    label: "Access token authorization hold",
    description: "Temporarily pauses Shopify API actions when token authorization needs attention.",
  },
  "Shopify.BulkSyncIntervalHours": {
    label: "Bulk sync interval (hours)",
    description: "Number of hours between scheduled Shopify product bulk sync runs.",
  },
  "Shopify.EnableInventorySync": {
    label: "Enable inventory sync",
    description: "Enables syncing inventory quantities from PartFinder to Shopify.",
  },
  "Shopify.EnableLocationSync": {
    label: "Enable location sync",
    description: "Enables syncing Shopify inventory locations and location mappings.",
  },
  "Shopify.EnableProductUpdatesWebhook": {
    label: "Enable product updates webhook handling",
    description: "Processes product update webhook events received from Shopify.",
  },
  "Shopify.EnableWebhooks": {
    label: "Enable webhook subscriptions",
    description: "Allows webhook subscription management and webhook-driven sync flows.",
  },
  "Shopify.InventoryAutoMapSyncIntervalHours": {
    label: "Inventory auto-map sync interval (hours)",
    description: "Number of hours between automatic inventory mapping sync attempts.",
  },
  "Shopify.InventoryBulkSyncIntervalHours": {
    label: "Inventory bulk sync interval (hours)",
    description: "Number of hours between full inventory bulk synchronization runs.",
  },
  "Shopify.LocationSyncIntervalHours": {
    label: "Location sync interval (hours)",
    description: "Number of hours between scheduled Shopify location synchronization runs.",
  },
  "Shopify.RequestFullProductSync": {
    label: "Request full product sync",
    description: "Flag used to queue a full product resync on the next sync cycle.",
  },
  "Shopify.WebhookSurgeModeActive": {
    label: "Webhook surge mode active",
    description: "Indicates whether webhook surge protection mode is currently active.",
  },
  "Turn14.EnableMetafieldSync": {
    label: "Enable Turn14 metafield sync",
    description: "Enables syncing Turn14-related data into Shopify metafields.",
  },
  "Turn14.EnableMpnTitleSync": {
    label: "Enable Turn14 MPN title sync",
    description: "Enables updating product titles using Turn14 MPN-based title rules.",
  },
};

const formatSyncSettingLabel = (key: string): string => {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getSyncSettingDescription = (key: string): string => {
  const curated = syncSettingMetadata[key]?.description;
  if (curated) return curated;
  return "Store-specific sync value used by backend synchronization logic.";
};

const extractApiErrorMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;

  const responseData = error.response?.data;
  if (typeof responseData === "string" && responseData.trim().length > 0) {
    return responseData.trim();
  }

  if (
    responseData
    && typeof responseData === "object"
    && "message" in responseData
    && typeof responseData.message === "string"
    && responseData.message.trim().length > 0
  ) {
    return responseData.message.trim();
  }

  return null;
};


export default function AdminStoreDetailsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [details, setDetails] = useState<AdminStoreDetails | null>(null);
  const [runtime, setRuntime] = useState<AdminShopRuntime | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRuntime, setLoadingRuntime] = useState(false);
  const [useExtendedAccessTokenForRuntime, setUseExtendedAccessTokenForRuntime] = useState(true);
  const [savingActiveState, setSavingActiveState] = useState(false);
  const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null);
  const [deletingAllWebhooks, setDeletingAllWebhooks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [savingSettingKey, setSavingSettingKey] = useState<string | null>(null);
  const [generalSettings, setGeneralSettings] = useState<AdminStoreGeneralSettings>({
    searchEnablePersistentFiltering: false,
    useYmmPrefixForProductTags: true,
  });
  const [savingGeneralSettings, setSavingGeneralSettings] = useState(false);
  const [atdProfile, setAtdProfile] = useState<AdminAtdProfile>(defaultAtdProfile);
  const [savingAtdProfile, setSavingAtdProfile] = useState(false);
  const [testingAtdProfile, setTestingAtdProfile] = useState(false);
  const [atdTestResult, setAtdTestResult] = useState<AdminAtdConnectionTestResult | null>(null);
  const [testingAtdFilterProducts, setTestingAtdFilterProducts] = useState(false);
  const [atdFilterProductSamples, setAtdFilterProductSamples] = useState<AdminAtdFilteredProductSample[]>([]);
  const [atdTestPartNumber, setAtdTestPartNumber] = useState("");
  const [creatingAtdTestProduct, setCreatingAtdTestProduct] = useState(false);
  const [atdTestProductResult, setAtdTestProductResult] = useState<AdminAtdCreateTestProductResponse | null>(null);
  const [clearingSurgeMode, setClearingSurgeMode] = useState(false);
  const [downloadingImportQueueId, setDownloadingImportQueueId] = useState<number | null>(null);
  const [deletingImportQueueId, setDeletingImportQueueId] = useState<number | null>(null);
  const [restartingImportQueueId, setRestartingImportQueueId] = useState<number | null>(null);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const parsedShopId = Number(shopId);

  const sortedSettings = useMemo(() => {
    if (!details) return [];
    return Object.entries(details.store.syncSettings).sort(([left], [right]) => left.localeCompare(right));
  }, [details]);

  const loadDetails = useCallback(async () => {
    if (!Number.isFinite(parsedShopId)) {
      setError("Invalid store id.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.storeDetails(token, parsedShopId);
      setDetails(response);
      setEditedSettings(response.store.syncSettings || {});
      setGeneralSettings(response.generalSettings || {
        searchEnablePersistentFiltering: false,
        useYmmPrefixForProductTags: true,
      });
      setAtdProfile(response.atdProfile || defaultAtdProfile);
      setAtdTestResult(null);
      setAtdFilterProductSamples([]);
      setAtdTestProductResult(null);
      setAtdTestPartNumber("");
      setLastLoadedAt(new Date());
    } catch {
      setError("Unable to load store details.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [parsedShopId, token, onUnauthorized]);

  const loadRuntime = useCallback(async (silent = false, useExtendedAccessToken = useExtendedAccessTokenForRuntime) => {
    if (!Number.isFinite(parsedShopId)) return;

    if (!silent) setLoadingRuntime(true);

    try {
      const response = await agent.Admin.shopRuntime(token, parsedShopId, useExtendedAccessToken);
      setRuntime(response);
      if (!silent) setLastLoadedAt(new Date());
    } catch {
      if (!silent) setError("Unable to load Shopify runtime status.");
    } finally {
      if (!silent) setLoadingRuntime(false);
    }
  }, [parsedShopId, token, useExtendedAccessTokenForRuntime]);

  useEffect(() => {
    loadDetails();
    loadRuntime();
  }, [loadDetails, loadRuntime]);

  useEffect(() => {
    if (!Number.isFinite(parsedShopId)) return;
    const id = window.setInterval(() => {
      loadRuntime(true);
    }, 10000);

    return () => window.clearInterval(id);
  }, [parsedShopId, loadRuntime]);

  const handleIsActiveChange = async (newValue: boolean) => {
    if (!details) return;

    setSavingActiveState(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreActiveStatus(token, details.store.shopId, newValue);
      setDetails((current) => current ? { ...current, store: { ...current.store, isActive: updated.isActive, updatedAt: updated.updatedAt } } : current);
    } catch {
      setError("Unable to update active status.");
    } finally {
      setSavingActiveState(false);
    }
  };

  const handleSettingEdit = (key: string, value: string) => {
    setEditedSettings((current) => ({ ...current, [key]: value }));
  };

  const handleSettingSave = async (key: string) => {
    if (!details) return;

    const value = editedSettings[key] ?? "";
    setSavingSettingKey(key);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreSyncSetting(token, details.store.shopId, key, value);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
    } catch {
      setError(`Unable to update setting: ${key}.`);
    } finally {
      setSavingSettingKey(null);
    }
  };


  const handleGeneralSettingChange = (key: keyof AdminStoreGeneralSettings, value: boolean) => {
    setGeneralSettings((current) => ({ ...current, [key]: value }));
  };

  const handleGeneralSettingsSave = async () => {
    if (!details) return;

    setSavingGeneralSettings(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreGeneralSettings(token, details.store.shopId, generalSettings);
      setGeneralSettings(updated);
      setDetails((current) => current ? { ...current, generalSettings: updated } : current);
    } catch {
      setError("Unable to update general store settings.");
    } finally {
      setSavingGeneralSettings(false);
    }
  };

  const handleClearSurgeMode = async () => {
    if (!details) return;

    const confirmed = window.confirm("Clear webhook surge mode and reset surge counters for this store?");
    if (!confirmed) return;

    setClearingSurgeMode(true);
    setError(null);

    try {
      const updated = await agent.Admin.clearStoreSurgeMode(token, details.store.shopId);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
    } catch {
      setError("Unable to clear surge mode.");
    } finally {
      setClearingSurgeMode(false);
    }
  };

  const handleAtdProfileChange = <K extends keyof AdminAtdProfile>(key: K, value: AdminAtdProfile[K]) => {
    setAtdProfile((current) => ({ ...current, [key]: value }));
  };

  const handleAddAttributeMapping = () => {
    setAtdProfile((current) => ({
      ...current,
      attributeMetafieldMappings: [...(current.attributeMetafieldMappings || []), { atdAttributeName: "", shopifyProductMetafield: "", shopifyMetafieldType: "single_line_text_field" }],
    }));
  };

  const handleUpdateAttributeMapping = (index: number, field: keyof AdminAtdAttributeMetafieldMapping, value: string) => {
    setAtdProfile((current) => {
      const next = [...(current.attributeMetafieldMappings || [])];
      if (!next[index]) return current;
      next[index] = { ...next[index], [field]: value };
      return { ...current, attributeMetafieldMappings: next };
    });
  };

  const handleRemoveAttributeMapping = (index: number) => {
    setAtdProfile((current) => ({
      ...current,
      attributeMetafieldMappings: (current.attributeMetafieldMappings || []).filter((_, i) => i !== index),
    }));
  };

  const handleSaveAtdProfile = async () => {
    if (!details) return;

    // Save and test are intentionally separate so credential edits can be staged without
    // overwriting the last saved values unless the user explicitly persists them.
    setSavingAtdProfile(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreAtdProfile(token, details.store.shopId, atdProfile);
      setAtdProfile(updated);
      setDetails((current) => current ? { ...current, atdProfile: updated } : current);
    } catch {
      setError("Unable to save ATD profile.");
    } finally {
      setSavingAtdProfile(false);
    }
  };

  const handleTestAtdProfile = async () => {
    if (!details) return;

    // Test against the current form state, not just the last saved state, so credentials can be
    // validated before saving them for the store.
    setTestingAtdProfile(true);
    setError(null);

    try {
      const result = await agent.Admin.testStoreAtdProfile(token, details.store.shopId, atdProfile);
      setAtdTestResult(result);
    } catch {
      setError("Unable to test ATD connection.");
    } finally {
      setTestingAtdProfile(false);
    }
  };

  const handleTestAtdFilterProducts = async () => {
    if (!details) return;

    setTestingAtdFilterProducts(true);
    setError(null);

    try {
      const result = await agent.Admin.testStoreAtdProfileProducts(token, details.store.shopId, atdProfile);
      setAtdFilterProductSamples(result);
    } catch {
      setError("Unable to test ATD size filters.");
    } finally {
      setTestingAtdFilterProducts(false);
    }
  };

  const handleCreateAtdTestProduct = async () => {
    if (!details) return;

    // Keep validation client-side too so operators get immediate feedback
    // before triggering the server-side ATD/Shopify sync call.
    const trimmedPartNumber = atdTestPartNumber.trim();
    if (!trimmedPartNumber) {
      setError("Enter an ATD part number before creating a test product.");
      return;
    }

    setCreatingAtdTestProduct(true);
    setError(null);

    try {
      const result = await agent.Admin.createStoreAtdTestProduct(
        token,
        details.store.shopId,
        trimmedPartNumber
      );
      setAtdTestProductResult(result);
    } catch (error) {
      const apiErrorMessage = extractApiErrorMessage(error);
      setError(apiErrorMessage ?? "Unable to create ATD test product.");
    } finally {
      setCreatingAtdTestProduct(false);
    }
  };


  const handleDeleteWebhook = async (id: string) => {
    if (!Number.isFinite(parsedShopId)) return;

    const confirmed = window.confirm(`Delete webhook subscription ${id}?`);
    if (!confirmed) return;

    setDeletingWebhookId(id);
    setError(null);

    try {
      await agent.Admin.deleteWebhookSubscription(token, parsedShopId, id);
      await loadRuntime();
    } catch {
      setError("Unable to delete webhook subscription.");
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const handleDeleteAllWebhooks = async () => {
    if (!Number.isFinite(parsedShopId) || !runtime || runtime.webhookSubscriptions.length === 0) return;

    const confirmed = window.confirm(`Delete all ${runtime.webhookSubscriptions.length} webhook subscriptions?`);
    if (!confirmed) return;

    setDeletingAllWebhooks(true);
    setError(null);

    try {
      for (const subscription of runtime.webhookSubscriptions) {
        await agent.Admin.deleteWebhookSubscription(token, parsedShopId, subscription.id);
      }

      await loadRuntime();
    } catch {
      setError("Unable to delete all webhook subscriptions.");
    } finally {
      setDeletingAllWebhooks(false);
    }
  };



  const handleDownloadFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    setDownloadingImportQueueId(item.queueId);
    setError(null);

    try {
      const response = await agent.Admin.downloadStoreFitmentImportFile(token, details.store.shopId, item.queueId);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.originalFileName || `fitment-import-${item.queueId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setError("Unable to download fitment import file.");
    } finally {
      setDownloadingImportQueueId(null);
    }
  };

  const handleAddNote = async () => {
    if (!details) return;

    const trimmedNote = newNote.trim();
    if (!trimmedNote) {
      setError("Note cannot be empty.");
      return;
    }

    setSavingNote(true);
    setError(null);

    try {
      const added = await agent.Admin.addStoreNote(token, details.store.shopId, trimmedNote);
      setDetails((current) => current
        ? {
            ...current,
            notes: [added, ...(current.notes || [])],
          }
        : current);
      setNewNote("");
    } catch {
      setError("Unable to save note.");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    const confirmed = window.confirm(`Delete fitment import history item ${item.queueId}?`);
    if (!confirmed) return;

    setDeletingImportQueueId(item.queueId);
    setError(null);

    try {
      await agent.Admin.deleteStoreFitmentImportHistoryItem(token, details.store.shopId, item.queueId);
      setDetails((current) => current
        ? {
            ...current,
            fitmentImportHistory: current.fitmentImportHistory.filter((historyItem) => historyItem.queueId !== item.queueId),
          }
        : current);
    } catch {
      setError("Unable to delete fitment import history item.");
    } finally {
      setDeletingImportQueueId(null);
    }
  };

  const handleRestartFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!token || !shopId) {
      return;
    }

    const confirmed = window.confirm(`Restart fitment import ${item.queueId} using the existing uploaded file?`);
    if (!confirmed) {
      return;
    }

    setRestartingImportQueueId(item.queueId);
    setError(null);

    try {
      await agent.Admin.restartStoreFitmentImportHistoryItem(token, Number(shopId), item.queueId);
      await loadDetails();
    } catch (restartError) {
      console.error(restartError);
      setError("Unable to restart fitment import history item.");
    } finally {
      setRestartingImportQueueId(null);
    }
  };


  const handleUseExtendedAccessTokenForRuntimeChange = (checked: boolean) => {
    setUseExtendedAccessTokenForRuntime(checked);
    void loadRuntime(false, checked);
  };

  const forceRefresh = async () => {
    await Promise.all([loadDetails(), loadRuntime()]);
  };

  const tabs = [
    { id: "overview", content: "Overview", accessibilityLabel: "Overview" },
    { id: "runtime", content: "Shopify runtime", accessibilityLabel: "Shopify runtime" },
    { id: "settings", content: "Sync settings", accessibilityLabel: "Sync settings" },
    { id: "atd", content: "ATD", accessibilityLabel: "ATD" },
    { id: "notes", content: "Notes", accessibilityLabel: "Notes" },
    { id: "fitment-history", content: "Fitment history", accessibilityLabel: "Fitment history" },
  ];

  const endpointText = (sub: AdminShopRuntime["webhookSubscriptions"][number]) => {
    if (sub.callbackUrl) return sub.callbackUrl;
    if (sub.arn) return sub.arn;
    if (sub.pubSubProject || sub.pubSubTopic) return `${sub.pubSubProject || "-"} / ${sub.pubSubTopic || "-"}`;
    return "-";
  };

  const alerts = useMemo(() => {
    if (!details) return [];

    const parseSettingBool = (key: string) => {
      const rawValue = details.store.syncSettings[key];
      if (!rawValue) return false;

      const normalized = rawValue.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes";
    };

    const nextAlerts: Array<{ title: string; tone: "warning" | "critical" | "info"; body: string }> = [];

    if (parseSettingBool("Shopify.WebhookSurgeModeActive")) {
      nextAlerts.push({
        title: "Webhook surge mode is active",
        tone: "critical",
        body: "Incoming webhook processing may be intentionally skipped while surge mode remains enabled.",
      });
    }

    if (!parseSettingBool("Shopify.EnableWebhooks")) {
      nextAlerts.push({
        title: "Webhook processing is disabled",
        tone: "warning",
        body: "This store is configured with Shopify webhooks disabled, so near real-time product updates will not run.",
      });
    }

    if (parseSettingBool("Shopify.AccessTokenAuthorizationHold")) {
      nextAlerts.push({
        title: "Shop access token is on authorization hold",
        tone: "critical",
        body: "The store token is currently held; Shopify API operations may be blocked until authorization is restored.",
      });
    }

    if (parseSettingBool("Shopify.RequestFullProductSync")) {
      nextAlerts.push({
        title: "Full product sync requested",
        tone: "info",
        body: "A full product sync has been requested and may increase processing load until completed.",
      });
    }

    if (runtime?.currentBulkOperation?.errorCode) {
      nextAlerts.push({
        title: "Current bulk operation has an error",
        tone: "critical",
        body: `Shopify reports error code: ${runtime.currentBulkOperation.errorCode}.`,
      });
    }

    return nextAlerts;
  }, [details, runtime]);

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <Button variant="plain" onClick={() => navigate("/pf-console/stores")}>← Back to stores</Button>
        <InlineStack gap="200">
          <Button onClick={loadDetails} loading={loading}>Refresh details</Button>
          <Button onClick={() => loadRuntime()} loading={loadingRuntime}>Refresh runtime</Button>
          <Button variant="primary" onClick={forceRefresh} loading={loading || loadingRuntime}>Force refresh (bypass stale view)</Button>
        </InlineStack>
      </InlineStack>

      {error ? <Text as="p" tone="critical">{error}</Text> : null}
      <Text as="p" tone="subdued">Store details are loaded into the page state; use refresh buttons to reload latest DB/runtime values. Last refreshed: {lastLoadedAt ? lastLoadedAt.toLocaleString() : "not yet"}.</Text>

      {loading ? (
        <Card><Text as="p">Loading store details...</Text></Card>
      ) : !details ? (
        <Card><Text as="p">No details found.</Text></Card>
      ) : (
        <>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingLg">{details.store.name || details.store.domain || `Shop ${details.store.shopId}`}</Text>
              <InlineStack gap="200">
                <Badge tone={details.store.isActive ? "success" : "warning"}>{details.store.isActive ? "Active" : "Inactive"}</Badge>
                <Text as="span" tone="subdued">{details.store.domain || details.store.myShopifyDomain || "-"}</Text>
              </InlineStack>
              {alerts.map((alert) => (
                <Banner key={alert.title} title={alert.title} tone={alert.tone}>
                  <p>{alert.body}</p>
                </Banner>
              ))}
            </BlockStack>
          </Card>

          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
            <Divider />
            <div style={{ paddingTop: "0.75rem" }}>
              {selectedTab === 0 ? (
                <BlockStack gap="400">
                  <DataTable
                    columnContentTypes={["text", "text"]}
                    headings={["Field", "Value"]}
                    rows={[
                      ["Domain", details.store.domain || details.store.myShopifyDomain || "-"],
                      ["Subscription", `${details.store.subscription?.name || "-"} (${details.store.subscription?.status || "no status"})`],
                      ["Plan", `${details.store.planDisplayName || "-"} | Dev: ${String(details.store.planPartnerDevelopment ?? false)} | Plus: ${String(details.store.planShopifyPlus ?? false)}`],
                      ["Emails", `${details.store.email || "-"} / ${details.store.contactEmail || "-"}`],
                      ["Product count", `${details.store.productCount}`],
                      ["Last sync", formatLocalDateTime(details.store.lastSyncAt)],
                      ["User last seen", formatLocalDateTime(details.store.userLastAccessedAt)],
                      ["Shop updated", formatLocalDateTime(details.store.updatedAt)],
                    ]}
                  />
                  <Checkbox
                    label="Shop is active"
                    checked={details.store.isActive}
                    onChange={handleIsActiveChange}
                    disabled={savingActiveState}
                  />
                </BlockStack>
              ) : null}

              {selectedTab === 1 ? (
                <BlockStack gap="400">
                  <Text as="p" tone="subdued">Auto-refreshes every 10 seconds.</Text>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Access scopes</Text>
                        <Badge tone={runtime?.usingExtendedAccessToken ? "success" : "info"}>
                          {runtime?.usingExtendedAccessToken ? "Using extended token" : "Using standard token"}
                        </Badge>
                      </InlineStack>
                      <Checkbox
                        label="Use extended access token (if available)"
                        checked={useExtendedAccessTokenForRuntime}
                        onChange={handleUseExtendedAccessTokenForRuntimeChange}
                        disabled={loadingRuntime}
                      />
                      {!runtime || runtime.accessScopes.length === 0 ? (
                        <Text as="p">No access scopes returned by Shopify.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text"]}
                          headings={["Scope"]}
                          rows={runtime.accessScopes.map((scope) => [scope])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Current bulk operation</Text>
                      {runtime?.currentBulkOperation ? (
                        <DataTable
                          columnContentTypes={["text", "text"]}
                          headings={["Field", "Value"]}
                          rows={[
                            ["ID", runtime.currentBulkOperation.id || "-"],
                            ["Status", runtime.currentBulkOperation.status || "-"],
                            ["Error", runtime.currentBulkOperation.errorCode || "-"],
                            ["Created", formatLocalDateTime(runtime.currentBulkOperation.createdAt)],
                            ["Completed", formatLocalDateTime(runtime.currentBulkOperation.completedAt)],
                            ["Object count", `${runtime.currentBulkOperation.objectCount ?? "-"}`],
                            ["File size", `${runtime.currentBulkOperation.fileSize ?? "-"}`],
                            ["Result URL", runtime.currentBulkOperation.url || "-"],
                            ["Partial data URL", runtime.currentBulkOperation.partialDataUrl || "-"],
                          ]}
                        />
                      ) : (
                        <Text as="p">No bulk operation currently returned by Shopify.</Text>
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Webhook subscriptions</Text>
                        <Button
                          tone="critical"
                          disabled={!runtime || runtime.webhookSubscriptions.length === 0 || deletingAllWebhooks || deletingWebhookId !== null}
                          loading={deletingAllWebhooks}
                          onClick={handleDeleteAllWebhooks}
                        >
                          Delete all
                        </Button>
                      </InlineStack>
                      {!runtime || runtime.webhookSubscriptions.length === 0 ? (
                        <Text as="p">No webhook subscriptions found.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Topic", "Endpoint type", "Endpoint", "Action"]}
                          rows={runtime.webhookSubscriptions.map((sub) => [
                            sub.topic,
                            sub.endpointType || "-",
                            endpointText(sub),
                            <Button
                              key={`delete-${sub.id}`}
                              variant="plain"
                              tone="critical"
                              disabled={deletingAllWebhooks || deletingWebhookId === sub.id}
                              onClick={() => handleDeleteWebhook(sub.id)}
                            >
                              Delete
                            </Button>
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : null}

              {selectedTab === 2 ? (
                <BlockStack gap="300">
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">MMY behavior settings</Text>
                      <Text as="p" tone="subdued">
                        Settings that control how make/model/year behavior works for storefront filtering and tag generation.
                      </Text>
                      <Checkbox
                        label="Enable persistent filtering on vehicle search"
                        checked={generalSettings.searchEnablePersistentFiltering}
                        onChange={(checked) => handleGeneralSettingChange("searchEnablePersistentFiltering", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <Checkbox
                        label="Use ymm_ prefix for product tags"
                        checked={generalSettings.useYmmPrefixForProductTags}
                        onChange={(checked) => handleGeneralSettingChange("useYmmPrefixForProductTags", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <InlineStack align="end">
                        <Button variant="primary" onClick={handleGeneralSettingsSave} loading={savingGeneralSettings} disabled={savingGeneralSettings}>
                          Save MMY behavior settings
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>

                  {sortedSettings.length === 0 ? (
                    <Text as="p">No explicit shop sync settings saved.</Text>
                  ) : (
                    <Card>
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="p" tone="subdued">Edit values and click save per row to update a store sync setting.</Text>
                          <Button
                            tone="critical"
                            onClick={handleClearSurgeMode}
                            loading={clearingSurgeMode}
                            disabled={clearingSurgeMode || savingSettingKey !== null}
                          >
                            Clear surge mode + counts
                          </Button>
                        </InlineStack>
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text", "text"]}
                          headings={["Label", "Setting name", "Description", "Value", "Action"]}
                          rows={sortedSettings.map(([key]) => {
                            const value = editedSettings[key] ?? "";
                            const isSaving = savingSettingKey === key;
                            const label = syncSettingMetadata[key]?.label ?? formatSyncSettingLabel(key);
                            const description = getSyncSettingDescription(key);

                            return [
                              label,
                              <code key={`name-${key}`}>{key}</code>,
                              description,
                              <div key={`value-${key}`} style={{ minWidth: "360px" }}>
                                <TextField
                                  label={`Value for ${key}`}
                                  labelHidden
                                  autoComplete="off"
                                  value={value}
                                  onChange={(nextValue) => handleSettingEdit(key, nextValue)}
                                  disabled={isSaving}
                                />
                              </div>,
                              <Button
                                key={`save-${key}`}
                                size="slim"
                                variant="primary"
                                onClick={() => handleSettingSave(key)}
                                loading={isSaving}
                                disabled={isSaving}
                              >
                                Save
                              </Button>
                            ];
                          })}
                        />
                      </BlockStack>
                    </Card>
                  )}
                </BlockStack>
              ) : null}


              {selectedTab === 3 ? (
                <BlockStack gap="300">
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">ATD profile</Text>
                        <Badge tone={atdProfile.isActive ? "success" : "attention"}>
                          {atdProfile.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </InlineStack>
                      <Text as="p" tone="subdued">
                        ATD connection settings are stored per store here so you can manage credentials and validate connectivity before the dedicated ATD migrations are applied.
                      </Text>
                      {atdTestResult ? (
                        <Banner
                          title={atdTestResult.isSuccess ? "ATD connection test succeeded" : "ATD connection test failed"}
                          tone={atdTestResult.isSuccess ? "success" : "critical"}
                        >
                          <p>{atdTestResult.message || "No message returned."}</p>
                          <p>Tested: {formatLocalDateTime(atdTestResult.testedAt)}</p>
                          <p>Auth endpoint: {atdTestResult.authEndpoint || "-"}</p>
                          <p>Catalog endpoint: {atdTestResult.catalogEndpoint || "-"}</p>
                          <p>Status: {atdTestResult.httpStatusCode ?? "-"}</p>
                          {atdTestResult.responseSnippet ? <p>Response: {atdTestResult.responseSnippet}</p> : null}
                        </Banner>
                      ) : null}
                      <TextField
                        label="Profile name"
                        autoComplete="off"
                        value={atdProfile.name}
                        onChange={(value) => handleAtdProfileChange("name", value)}
                      />
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "320px", flex: 1 }}>
                          <TextField
                            label="Base URL"
                            autoComplete="off"
                            value={atdProfile.baseUrl}
                            onChange={(value) => handleAtdProfileChange("baseUrl", value)}
                          />
                        </div>
                        <div style={{ minWidth: "140px" }}>
                          <TextField
                            label="API version"
                            autoComplete="off"
                            value={atdProfile.apiVersion}
                            onChange={(value) => handleAtdProfileChange("apiVersion", value)}
                          />
                        </div>
                        <div style={{ minWidth: "180px" }}>
                          <TextField
                            label="STH API version"
                            autoComplete="off"
                            value={atdProfile.shipToHomeApiVersion}
                            onChange={(value) => handleAtdProfileChange("shipToHomeApiVersion", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Client ID"
                            autoComplete="off"
                            value={atdProfile.clientId}
                            onChange={(value) => handleAtdProfileChange("clientId", value)}
                          />
                        </div>
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Username"
                            autoComplete="off"
                            value={atdProfile.username}
                            onChange={(value) => handleAtdProfileChange("username", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Password"
                            autoComplete="off"
                            type="password"
                            value={atdProfile.password}
                            onChange={(value) => handleAtdProfileChange("password", value)}
                          />
                        </div>
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Secret key"
                            autoComplete="off"
                            type="password"
                            value={atdProfile.secretKey}
                            onChange={(value) => handleAtdProfileChange("secretKey", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "220px", flex: 1 }}>
                          <TextField
                            label="Account number"
                            autoComplete="off"
                            value={atdProfile.accountNumber}
                            onChange={(value) => handleAtdProfileChange("accountNumber", value)}
                          />
                        </div>
                        <div style={{ minWidth: "220px", flex: 1 }}>
                          <TextField
                            label="Location number"
                            autoComplete="off"
                            value={atdProfile.locationNumber}
                            onChange={(value) => handleAtdProfileChange("locationNumber", value)}
                          />
                        </div>
                      </InlineStack>
                      <Checkbox
                        label="Use ship-to-home catalog"
                        checked={atdProfile.useShipToHomeCatalog}
                        onChange={(checked) => handleAtdProfileChange("useShipToHomeCatalog", checked)}
                      />
                      <Checkbox
                        label="ATD profile is active"
                        checked={atdProfile.isActive}
                        onChange={(checked) => handleAtdProfileChange("isActive", checked)}
                      />
                      <Checkbox
                        label="Enable ATD pull"
                        checked={atdProfile.enableAtdPull}
                        onChange={(checked) => handleAtdProfileChange("enableAtdPull", checked)}
                      />
                      <Checkbox
                        label="Enable ATD push to Shopify"
                        checked={atdProfile.enableAtdPushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enableAtdPushToShopify", checked)}
                      />
                      <Checkbox
                        label="Enable fitment pull"
                        checked={atdProfile.enableFitmentPull}
                        onChange={(checked) => handleAtdProfileChange("enableFitmentPull", checked)}
                        disabled={!atdProfile.enableAtdPull}
                      />
                      <Checkbox
                        label="Enable fitment push to Shopify"
                        checked={atdProfile.enableFitmentPushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enableFitmentPushToShopify", checked)}
                        disabled={!atdProfile.enableAtdPushToShopify}
                      />
                      <Checkbox
                        label="Enable price pull"
                        checked={atdProfile.enablePricePull}
                        onChange={(checked) => handleAtdProfileChange("enablePricePull", checked)}
                        disabled={!atdProfile.enableAtdPull}
                      />
                      <Checkbox
                        label="Enable price push to Shopify"
                        checked={atdProfile.enablePricePushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enablePricePushToShopify", checked)}
                        disabled={!atdProfile.enableAtdPushToShopify}
                      />
                      <Checkbox
                        label="Create new Shopify products as draft"
                        checked={atdProfile.createProductsAsDraft}
                        onChange={(checked) => handleAtdProfileChange("createProductsAsDraft", checked)}
                      />
                      <Checkbox
                        label="Enable test mode (limit new product creates)"
                        checked={atdProfile.testModeEnabled}
                        onChange={(checked) => handleAtdProfileChange("testModeEnabled", checked)}
                      />
                      <div style={{ maxWidth: "320px" }}>
                        <TextField
                          label="Test mode create limit"
                          type="number"
                          autoComplete="off"
                          value={String(atdProfile.testModeCreateLimit ?? 25)}
                          min={1}
                          disabled={!atdProfile.testModeEnabled}
                          helpText="When test mode is enabled, only this many new products are queued for Shopify create per prep run."
                          onChange={(value) => {
                            const parsed = Number.parseInt(value, 10);
                            handleAtdProfileChange("testModeCreateLimit", Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
                          }}
                        />
                      </div>
                      <TextField
                        label="Size attribute values to include"
                        autoComplete="off"
                        multiline={3}
                        helpText='Only products with attribute name "size" and one of these values will be eligible for sync. Enter comma or newline separated values.'
                        value={atdProfile.sizeAttributeValues.join(", ")}
                        onChange={(value) => handleAtdProfileChange("sizeAttributeValues", value.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean))}
                      />
                      <Divider />
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="h4" variant="headingSm">ATD attribute to Shopify metafield mappings</Text>
                          <Button onClick={handleAddAttributeMapping}>Add mapping</Button>
                        </InlineStack>
                        <Text as="p" tone="subdued">Map an ATD attribute name to a Shopify product metafield using namespace.key format.</Text>
                        {(atdProfile.attributeMetafieldMappings || []).length === 0 ? (
                          <Text as="p" tone="subdued">No attribute mappings configured.</Text>
                        ) : (
                          <DataTable
                            columnContentTypes={["text", "text", "text", "text"]}
                            headings={["ATD attribute", "Shopify product metafield (namespace.key)", "Shopify type", "Action"]}
                            rows={(atdProfile.attributeMetafieldMappings || []).map((mapping, index) => ([
                              <TextField
                                key={`atd-attr-${index}`}
                                label={`ATD attribute ${index + 1}`}
                                labelHidden
                                autoComplete="off"
                                value={mapping.atdAttributeName || ""}
                                onChange={(value) => handleUpdateAttributeMapping(index, "atdAttributeName", value)}
                              />,
                              <TextField
                                key={`shopify-metafield-${index}`}
                                label={`Shopify metafield ${index + 1}`}
                                labelHidden
                                autoComplete="off"
                                value={mapping.shopifyProductMetafield || ""}
                                onChange={(value) => handleUpdateAttributeMapping(index, "shopifyProductMetafield", value)}
                                placeholder="namespace.key"
                              />,
                              <Select
                                key={`shopify-metafield-type-${index}`}
                                label={`Shopify metafield type ${index + 1}`}
                                labelHidden
                                options={atdMetafieldTypeOptions}
                                value={mapping.shopifyMetafieldType || "single_line_text_field"}
                                onChange={(value) => handleUpdateAttributeMapping(index, "shopifyMetafieldType", value)}
                              />,
                              <Button key={`remove-mapping-${index}`} tone="critical" onClick={() => handleRemoveAttributeMapping(index)}>
                                Remove
                              </Button>
                            ]))}
                          />
                        )}
                      </BlockStack>
                      {atdFilterProductSamples.length > 0 ? (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Part number", "Product", "Brand", "Size values"]}
                          rows={atdFilterProductSamples.map((item) => [
                            item.partNumber || "-",
                            item.productName || "-",
                            item.brandName || "-",
                            item.sizeValues.length > 0 ? item.sizeValues.join(", ") : "-"
                          ])}
                        />
                      ) : null}
                      <Divider />
                      <BlockStack gap="200">
                        <Text as="h4" variant="headingSm">Create test product</Text>
                        <Text as="p" tone="subdued">
                          Push one ATD product into Shopify using the existing ATD Shopify object-manager flow, then review source vs Shopify fields below.
                        </Text>
                        {/* This action intentionally reuses the standard backend ATD push pipeline (single-product scoped)
                            so the comparison output reflects real sync behavior. */}
                        <InlineStack gap="200" align="start" blockAlign="end">
                          <div style={{ minWidth: "280px", flex: 1 }}>
                            <TextField
                              label="ATD part number"
                              autoComplete="off"
                              value={atdTestPartNumber}
                              onChange={setAtdTestPartNumber}
                              placeholder="e.g. 12345"
                              disabled={creatingAtdTestProduct}
                            />
                          </div>
                          <Button
                            variant="primary"
                            onClick={handleCreateAtdTestProduct}
                            loading={creatingAtdTestProduct}
                            disabled={creatingAtdTestProduct || !atdTestPartNumber.trim()}
                          >
                            Create test product
                          </Button>
                        </InlineStack>
                        {atdTestProductResult ? (
                          <Card>
                            <BlockStack gap="200">
                              <Banner title={atdTestProductResult.message} tone={atdTestProductResult.shopify ? "success" : "critical"}>
                                <p>Part number: {atdTestProductResult.partNumber}</p>
                                <p>ATD product ID: {atdTestProductResult.atdProductId}</p>
                                <p>Shopify product ID: {atdTestProductResult.shopifyProductId ?? "-"}</p>
                                {(atdTestProductResult.diagnostics || []).map((line, index) => (
                                  <p key={`atd-test-diagnostic-${index}`}>{line}</p>
                                ))}
                              </Banner>
                              <Text as="h5" variant="headingSm">ATD source data</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Field", "Value"]}
                                rows={[
                                  ["Product name", atdTestProductResult.source.productName || "-"],
                                  ["Brand", atdTestProductResult.source.brandName || "-"],
                                  ["Category", atdTestProductResult.source.categoryName || "-"],
                                  ["Discontinued", atdTestProductResult.source.isDiscontinued ? "Yes" : "No"],
                                  ["Available quantity", String(atdTestProductResult.source.availableQuantity ?? "-")],
                                  ["Weight", String(atdTestProductResult.source.weight ?? "-")],
                                  ["Primary image", atdTestProductResult.source.primaryImageUrl || "-"],
                                ]}
                              />
                              <Text as="h5" variant="headingSm">ATD attributes (sample)</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Attribute", "Value"]}
                                rows={(atdTestProductResult.source.attributes || []).length > 0
                                  ? atdTestProductResult.source.attributes.map((x) => [x.name || "-", x.value || "-"])
                                  : [["-", "No attributes"]]}
                              />
                              <Text as="h5" variant="headingSm">ATD prices (sample)</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Price type", "Amount"]}
                                rows={(atdTestProductResult.source.prices || []).length > 0
                                  ? atdTestProductResult.source.prices.map((x) => [x.priceType || "-", String(x.amount ?? "-")])
                                  : [["-", "No prices"]]}
                              />
                              <Text as="h5" variant="headingSm">Shopify product snapshot</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Field", "Value"]}
                                rows={atdTestProductResult.shopify ? [
                                  ["Product ID", String(atdTestProductResult.shopify.productId)],
                                  ["Title", atdTestProductResult.shopify.title || "-"],
                                  ["Vendor", atdTestProductResult.shopify.vendor || "-"],
                                  ["Product type", atdTestProductResult.shopify.productType || "-"],
                                  ["Handle", atdTestProductResult.shopify.handle || "-"],
                                  ["Status", atdTestProductResult.shopify.status || "-"],
                                  ["Variant SKU", atdTestProductResult.shopify.variantSku || "-"],
                                  ["Metafield length", String(atdTestProductResult.shopify.metafieldLength ?? "-")],
                                  ["Metafield width", String(atdTestProductResult.shopify.metafieldWidth ?? "-")],
                                  ["Metafield height", String(atdTestProductResult.shopify.metafieldHeight ?? "-")],
                                  ["Metafield weight", String(atdTestProductResult.shopify.metafieldWeight ?? "-")],
                                  ["Tags", atdTestProductResult.shopify.tags?.length ? atdTestProductResult.shopify.tags.join(", ") : "-"],
                                ] : [["-", "No Shopify product data returned"]]}
                              />
                            </BlockStack>
                          </Card>
                        ) : null}
                      </BlockStack>
                      <InlineStack align="end" gap="200">
                        <Button onClick={handleTestAtdFilterProducts} loading={testingAtdFilterProducts} disabled={testingAtdFilterProducts || savingAtdProfile}>
                          Test product filters
                        </Button>
                        <Button onClick={handleTestAtdProfile} loading={testingAtdProfile} disabled={testingAtdProfile || savingAtdProfile}>
                          Test connection
                        </Button>
                        <Button variant="primary" onClick={handleSaveAtdProfile} loading={savingAtdProfile} disabled={savingAtdProfile || testingAtdProfile}>
                          Save ATD profile
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : null}

              {selectedTab === 4 ? (
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">Store notes</Text>
                  <TextField
                    label="Add note"
                    multiline={4}
                    autoComplete="off"
                    value={newNote}
                    onChange={setNewNote}
                    disabled={savingNote}
                  />
                  <InlineStack align="end">
                    <Button
                      variant="primary"
                      onClick={handleAddNote}
                      loading={savingNote}
                      disabled={savingNote || !newNote.trim()}
                    >
                      Add note
                    </Button>
                  </InlineStack>
                  {details.notes.length === 0 ? (
                    <Text as="p">No notes for this store yet.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={["text", "text", "text"]}
                      headings={["Created", "Created by", "Note"]}
                      rows={details.notes.map((note: AdminStoreNote) => [
                        formatLocalDateTime(note.createdAt),
                        note.createdByAppUserId || "-",
                        note.note,
                      ])}
                    />
                  )}
                </BlockStack>
              ) : null}

              {selectedTab === 5 ? (
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">Fitment import history</Text>
                  {details.fitmentImportHistory.length === 0 ? (
                    <Text as="p">No fitment imports found for this store.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={["text", "text", "text", "text", "text", "text"]}
                      headings={["Queued", "File", "Format", "State", "Completed", "Action"]}
                      rows={details.fitmentImportHistory.map((item) => [
                        formatLocalDateTime(item.addedOn),
                        item.originalFileName || item.storedFileName || `Import ${item.queueId}`,
                        item.importFormat,
                        item.state,
                        formatLocalDateTime(item.completedAt),
                        <InlineStack key={`actions-${item.queueId}`} gap="200">
                          <Button
                            key={`download-${item.queueId}`}
                            size="slim"
                            onClick={() => handleDownloadFitmentImport(item)}
                            loading={downloadingImportQueueId === item.queueId}
                            disabled={downloadingImportQueueId !== null || deletingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Download
                          </Button>
                          <Button
                            key={`restart-${item.queueId}`}
                            size="slim"
                            onClick={() => handleRestartFitmentImport(item)}
                            loading={restartingImportQueueId === item.queueId}
                            disabled={deletingImportQueueId !== null || downloadingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Restart
                          </Button>
                          <Button
                            key={`delete-${item.queueId}`}
                            size="slim"
                            tone="critical"
                            onClick={() => handleDeleteFitmentImport(item)}
                            loading={deletingImportQueueId === item.queueId}
                            disabled={deletingImportQueueId !== null || downloadingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Delete
                          </Button>
                        </InlineStack>
                      ])}
                    />
                  )}
                </BlockStack>
              ) : null}
            </div>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
